import React, { useEffect, useState } from "react";
import { ShieldCheck, ShieldAlert, FileText, FileJson, Loader2, Sparkles, Award, Download, Link2 } from "lucide-react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { toast } from "sonner";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

const SOCIALS = [
  { key: "x", label: "X", href: (u, t) => `https://twitter.com/intent/tweet?url=${u}&text=${t}` },
  { key: "facebook", label: "Facebook", href: (u) => `https://www.facebook.com/sharer/sharer.php?u=${u}` },
  { key: "reddit", label: "Reddit", href: (u, t) => `https://www.reddit.com/submit?url=${u}&title=${t}` },
  { key: "whatsapp", label: "WhatsApp", href: (u, t) => `https://wa.me/?text=${t}%20${u}` },
  { key: "telegram", label: "Telegram", href: (u, t) => `https://t.me/share/url?url=${u}&text=${t}` },
];

export const LicenceProvenanceCard = ({ gameId }) => {
  const [cert, setCert] = useState(null);
  const [err, setErr] = useState(false);
  const [shareOpen, setShareOpen] = useState(false);

  useEffect(() => {
    let alive = true;
    api.get(`/rights/certificate/${gameId}`)
      .then((r) => { if (alive) setCert(r.data); })
      .catch(() => { if (alive) setErr(true); });
    return () => { alive = false; };
  }, [gameId]);

  const download = (text, filename, type) => {
    const blob = new Blob([text], { type });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = filename; document.body.appendChild(a); a.click(); a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 2000);
  };

  const certImg = `${BACKEND}/api/og/game/${gameId}/certificate.png`;
  const shareUrl = `${window.location.origin}/g/${gameId}`;

  const downloadImage = async () => {
    try {
      const res = await fetch(certImg);
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = `${slug}_certificate.png`; document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 2000);
    } catch { window.open(certImg, "_blank"); }
  };

  const copyLink = async () => {
    try { await navigator.clipboard.writeText(shareUrl); toast.success("Share link copied! 🔗"); } catch { toast(shareUrl); }
  };

  if (err) return null;

  const clean = cert?.manifest?.ip_scan?.clean;
  const m = cert?.manifest;
  const slug = (m?.game?.title || "game").replace(/\s+/g, "_");

  return (
    <div className="rounded-2xl border border-[hsl(var(--border))] bg-[hsl(var(--card))] p-5" data-testid="licence-cert">
      <h3 className="font-display font-bold mb-1 flex items-center gap-2">
        <ShieldCheck className="w-5 h-5 text-emerald-500" /> Licence &amp; Provenance
      </h3>
      {!cert || !m ? (
        <div className="flex items-center gap-2 text-sm text-[hsl(var(--muted-foreground))] py-2"><Loader2 className="w-4 h-4 animate-spin" /> Loading certificate…</div>
      ) : (
        <>
          <p className="text-xs text-[hsl(var(--muted-foreground))] mb-3">
            A verifiable ownership certificate travels with this game — it ships inside every engine export.
          </p>
          <div className="space-y-2 text-sm">
            <div className="flex items-center justify-between gap-3">
              <span className="text-[hsl(var(--muted-foreground))]">Certificate</span>
              <span className="font-mono text-xs font-semibold" data-testid="licence-cert-id">{m.certificate_id || "—"}</span>
            </div>
            <div className="flex items-center justify-between gap-3">
              <span className="text-[hsl(var(--muted-foreground))]">Licence</span>
              <span className="font-semibold text-right">{m.licence?.label || "All Rights Reserved"}</span>
            </div>
            <div className="flex items-center justify-between gap-3">
              <span className="text-[hsl(var(--muted-foreground))]">Provenance</span>
              <span className="inline-flex items-center gap-1 font-semibold"><Sparkles className="w-3.5 h-3.5 text-[hsl(var(--primary))]" /> {m.provenance?.ai_generated_assets ?? 0}/{m.provenance?.total_assets ?? 0} AI-built</span>
            </div>
          </div>
          <div className={`mt-3 flex items-start gap-2 rounded-xl px-3 py-2 text-xs font-medium ${clean ? "bg-emerald-500/10 text-emerald-500" : "bg-amber-500/10 text-amber-500"}`} data-testid="licence-cert-ipscan">
            {clean ? <ShieldCheck className="w-4 h-4 mt-0.5 shrink-0" /> : <ShieldAlert className="w-4 h-4 mt-0.5 shrink-0" />}
            <span>{clean ? "IP scan passed — no known protected brands or characters detected." : `Review needed: ${(m.ip_scan?.matches || []).join(", ")}`}</span>
          </div>
          {m.remix && (
            <div className="mt-2 text-[11px] text-[hsl(var(--muted-foreground))]">
              🔀 Remix of “{m.remix.source_title}”{m.remix.source_creator ? ` by ${m.remix.source_creator}` : ""}
            </div>
          )}
          <div className="flex flex-wrap gap-2 mt-3">
            <Button size="sm" className="rounded-full bg-gradient-to-r from-[#c9a227] to-[#e0b84a] text-black hover:opacity-90" data-testid="licence-cert-share-image"
              onClick={() => setShareOpen(true)}>
              <Award className="w-4 h-4" /> Share certificate
            </Button>
            <Button size="sm" variant="outline" className="rounded-full" data-testid="licence-cert-download"
              onClick={() => download(cert.license_text, `${slug}_LICENSE.txt`, "text/plain")}>
              <FileText className="w-4 h-4" /> .txt
            </Button>
            <Button size="sm" variant="ghost" className="rounded-full text-[hsl(var(--muted-foreground))]" data-testid="licence-cert-download-json"
              onClick={() => download(JSON.stringify(m, null, 2), `${slug}_PROVENANCE.json`, "application/json")}>
              <FileJson className="w-4 h-4" /> JSON
            </Button>
          </div>
          <a href={`/verify/${m.certificate_id}`} target="_blank" rel="noopener noreferrer" data-testid="licence-cert-verify-link"
            className="inline-flex items-center gap-1 mt-2.5 text-xs text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--primary))] transition-colors">
            <ShieldCheck className="w-3.5 h-3.5" /> Public verification page ↗
          </a>

          <Dialog open={shareOpen} onOpenChange={setShareOpen}>
            <DialogContent className="max-w-lg" data-testid="certificate-image-dialog">
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2"><Award className="w-5 h-5 text-amber-500" /> Certificate of Authenticity</DialogTitle>
                <DialogDescription>Show the world your game is verified original. Download the badge or post it to your community.</DialogDescription>
              </DialogHeader>
              <div className="rounded-xl overflow-hidden border border-[hsl(var(--border))] bg-black">
                <img src={certImg} alt="Certificate of Authenticity" className="w-full block" data-testid="certificate-image" loading="eager" />
              </div>
              <div className="flex flex-wrap gap-2">
                <Button size="sm" className="rounded-full" onClick={downloadImage} data-testid="certificate-download-image">
                  <Download className="w-4 h-4" /> Download image
                </Button>
                <Button size="sm" variant="outline" className="rounded-full" onClick={copyLink} data-testid="certificate-copy-link">
                  <Link2 className="w-4 h-4" /> Copy game link
                </Button>
              </div>
              <div className="flex flex-wrap gap-2 pt-1">
                {SOCIALS.map((s) => (
                  <a key={s.key} href={s.href(encodeURIComponent(shareUrl), encodeURIComponent(`I built a verified-original game on GameView Studio 🛡️`))}
                    target="_blank" rel="noopener noreferrer" data-testid={`certificate-social-${s.key}`}
                    className="px-3 py-1.5 rounded-full text-xs font-semibold border border-[hsl(var(--border))] hover:bg-[hsl(var(--secondary))] transition-colors">
                    {s.label}
                  </a>
                ))}
              </div>
            </DialogContent>
          </Dialog>
        </>
      )}
    </div>
  );
};

export default LicenceProvenanceCard;
